import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  WORD_LENGTH,
  mergeKeyboardStates,
  type AckResult,
  type GuessResponse,
  type LetterState,
  type MatchState,
  type OpponentWatchState,
} from "@mk/shared";
import { getSocket, serverNow } from "../../lib/socket.ts";
import { useBoardInput } from "../useBoardInput.ts";
import { createDraftRelay, toDraftCellsWire } from "./draftRelay.ts";
import { useDuelStore } from "./duelStore.ts";
import { gameEdition } from "../../lib/edition.ts";
import { messages } from "../../i18n/index.ts";
import { errorMessage, errors } from "../../i18n/index.ts";
import { playSound, stopTimerSounds } from "../../lib/sound.ts";
import { advanceDuelAudio, EMPTY_DUEL_AUDIO_STATE } from "./duelAudio.ts";
import { shouldAutoWatchOpponent } from "./autoOpponentWatch.ts";

/**
 * Düello ekranının görünüm katmanı.
 *
 * Soket olaylarını bu hook DİNLEMEZ — onlar `duelStore` içinde, oturum boyunca
 * kurulu duruyor. Burada yalnızca ekrana özel şeyler var: yazım, geri sayım,
 * uyarı balonu ve kullanıcı eylemleri.
 */
export function useDuel() {
  const phase = useDuelStore((s) => s.phase);
  const match = useDuelStore((s) => s.match);
  const rows = useDuelStore((s) => s.rows);
  const opponentRows = useDuelStore((s) => s.opponentRows);
  const opponentSolvedAt = useDuelStore((s) => s.opponentSolvedAt);
  const attemptLimit = useDuelStore((s) => s.attemptLimit);
  const ownRevealUntil = useDuelStore((s) => s.ownRevealUntil);
  const result = useDuelStore((s) => s.result);
  const watchers = useDuelStore((s) => s.watchers);
  const opponentWatch = useDuelStore((s) => s.opponentWatch);
  const opponentWatchRequestedFor = useDuelStore((s) => s.opponentWatchRequestedFor);
  const rematchOffer = useDuelStore((s) => s.rematchOffer);
  const rematchSentFor = useDuelStore((s) => s.rematchSentFor);
  const rematchNotice = useDuelStore((s) => s.rematchNotice);

  const [toast, setToast] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [opponentWatchBusy, setOpponentWatchBusy] = useState(false);
  const [, setRevealTick] = useState(0);
  const duelAudioState = useRef(EMPTY_DUEL_AUDIO_STATE);
  const solvedAudioState = useRef({
    matchId: null as string | null,
    solved: false,
    played: false,
  });

  const timers = useRef<number[]>([]);
  useEffect(
    () => () => {
      for (const id of timers.current) window.clearTimeout(id);
    },
    [],
  );

  const warn = useCallback((message: string) => {
    setToast(message);
    setShake(true);
    timers.current.push(window.setTimeout(() => setToast(null), 1700));
    timers.current.push(window.setTimeout(() => setShake(false), 440));
  }, []);

  /* --- Rakip kelimeyi buldu ------------------------------------------ */

  /*
   * Balon kaçabilir diye üç katman: balon + titreşim + hayalet tahtanın
   * altında kalıcı satır (DuelScreen). Rakibin bitirdiğini fark etmemek
   * düellonun en can alıcı bilgisini kaçırmak demek.
   */
  const opponentName = match?.opponent.username;
  useEffect(() => {
    if (opponentSolvedAt === null) return;
    warn(messages.feedback.opponentSolved(opponentName ?? messages.feedback.opponent, opponentSolvedAt));
    navigator.vibrate?.(200);
  }, [opponentSolvedAt, opponentName, warn]);

  /* --- Kopan bağlantıdan dönüş -------------------------------------- */

  // Ekran açıldığında sunucuya "bende maç var mı" diye sor. Olağan akışta
  // gerekmez ama sekme uzun süre kapalı kaldıysa tahtayı geri getirir.
  useEffect(() => {
    const socket = getSocket();
    socket.emit("match:sync", {}, (res: AckResult<MatchState>) => {
      if (res?.ok) {
        useDuelStore.getState().restoreFrom(res.data);
        return;
      }
      /*
       * Sunucuda maç yok. İstemci hâlâ tahtayı gösteriyorsa (sunucu yeniden
       * başlamış olabilir) oyuncuyu kilitli bırakmayalım.
       */
      const { phase, reset } = useDuelStore.getState();
      if (phase === "countdown" || phase === "live") reset();
    });
  }, []);

  /* --- Geri sayım: kaynak sunucunun `endsAt`i ------------------------ */

  useEffect(() => {
    if (!match || (phase !== "live" && phase !== "countdown")) return;

    const tick = () => {
      const now = serverNow();
      if (phase === "countdown" && now >= match.startsAt) {
        useDuelStore.getState().setPhase("live");
      }
      setRemainingMs(Math.max(0, match.endsAt - now));
    };

    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [match, phase]);

  useEffect(() => {
    const live = phase === "live" && result === null;
    const transition = advanceDuelAudio(duelAudioState.current, {
      matchId: match?.matchId ?? null,
      live,
      remainingMs,
      visible: typeof document === "undefined" || document.visibilityState === "visible",
    });
    duelAudioState.current = transition.state;
    if (!live) {
      stopTimerSounds();
      return;
    }
    for (const effect of transition.effects) playSound(effect);
  }, [match?.matchId, phase, remainingMs, result]);

  useEffect(() => () => stopTimerSounds(), []);

  /* --- Eylemler ------------------------------------------------------ */

  const findOpponent = useCallback(() => {
    useDuelStore.getState().setPhase("searching");
    getSocket().emit("queue:join", (res: AckResult<{ queued: true }>) => {
      if (!res?.ok) {
        useDuelStore.getState().setPhase("idle");
        warn(res ? errorMessage(res) : errors.match_start_failed());
      }
    });
  }, [warn]);

  const cancelSearch = useCallback(() => {
    getSocket().emit("queue:leave");
    useDuelStore.getState().setPhase("idle");
  }, []);

  const submit = useCallback(
    (raw: string | null) => {
      const current = useDuelStore.getState();
      if (!current.match || sendingRef.current) return;
      if (raw === null) {
        warn(messages.feedback.lengthRequired(WORD_LENGTH));
        return;
      }
      const word = gameEdition.normalizeWord(raw);
      sendingRef.current = true;
      setSending(true);
      /*
       * Zaman aşımı şart: ack'siz `emit`te bağlantı tam o anda koparsa geri
       * çağrı hiç çalışmıyor, `sending` true kalıyor ve klavye maçın sonuna
       * kadar kilitli duruyordu.
       */
      getSocket()
        .timeout(8000)
        .emit(
          "guess:submit",
          { matchId: current.match.matchId, word, row: current.rows.length },
          (timeout: Error | null, res: AckResult<GuessResponse>) => {
            sendingRef.current = false;
            setSending(false);
            if (timeout) {
              warn(messages.feedback.guessConnectionFailed);
              return;
            }
            if (!res?.ok) warn(res ? errorMessage(res) : errors.guess_process_failed());
            /*
             * Tahtayı BURADA güncellemiyoruz. Sunucu her kabul edilen tahminden
             * sonra `board:sync` ile tam tahtayı yolluyor; ikisini birden
             * uygularsak hangisi önce gelirse ona göre satır çiftleniyordu.
             * Tek doğruluk kaynağı: sunucunun tahtası.
             */
          },
        );
    },
    [warn],
  );

  const solved = rows.some((r) => r.pattern.every((s) => s === "correct"));
  const myTurnOver = solved || rows.length >= attemptLimit;
  const ownTurnRevealed = ownRevealUntil <= Date.now();

  useEffect(() => {
    const waitMs = ownRevealUntil - Date.now();
    if (waitMs <= 0) return;
    // Tarayıcı zamanlayıcısı birkaç ms erken uyanırsa kapı yeniden kapanmasın.
    const id = window.setTimeout(() => setRevealTick((value) => value + 1), waitMs + 16);
    return () => window.clearTimeout(id);
  }, [ownRevealUntil]);

  useEffect(() => {
    const matchId = match?.matchId ?? result?.matchId ?? null;
    if (!matchId) {
      solvedAudioState.current = { matchId: null, solved: false, played: false };
      return;
    }

    const ownSolved = solved || (result?.matchId === matchId && result.you.solved);
    const previous = solvedAudioState.current;
    if (previous.matchId !== matchId) {
      // Yeniden yüklenen bitmiş/çözülmüş bir maçı seslendirme.
      solvedAudioState.current = { matchId, solved: ownSolved, played: ownSolved };
      return;
    }
    if (!previous.solved && ownSolved && !previous.played) {
      playSound("solved");
      solvedAudioState.current = { matchId, solved: true, played: true };
      return;
    }
    solvedAudioState.current = { ...previous, solved: ownSolved };
  }, [match?.matchId, result, solved]);

  const input = useBoardInput({
    disabled: phase !== "live" || sending || myTurnOver,
    onSubmit: submit,
  });

  const rowCount = rows.length;
  const clearDraft = input.clear;
  const inputIdentity = `${match?.matchId ?? ""}:${rowCount}`;
  const [draftIdentity, setDraftIdentity] = useState(inputIdentity);
  useEffect(() => {
    if (draftIdentity === inputIdentity) return;
    clearDraft();
    setDraftIdentity(inputIdentity);
  }, [clearDraft, draftIdentity, inputIdentity]);

  // Authoritative tahta ack'ten önce geldiyse klavyeyi timeout'a kadar kilitleme.
  useEffect(() => {
    if (!sendingRef.current) return;
    sendingRef.current = false;
    setSending(false);
  }, [rowCount]);

  useEffect(() => {
    if (myTurnOver) clearDraft();
  }, [clearDraft, myTurnOver]);

  const draftRelay = useMemo(
    () =>
      createDraftRelay((payload) => {
        getSocket().emit("draft:update", payload);
      }),
    [],
  );

  useEffect(
    () => () => {
      draftRelay.stop();
    },
    [draftRelay],
  );

  useEffect(() => {
    if (!match) {
      draftRelay.update({
        enabled: false,
        matchId: "",
        row: rowCount,
        cells: toDraftCellsWire(input.cells),
      });
      return;
    }
    draftRelay.update({
      enabled:
        phase === "live" &&
        !myTurnOver &&
        (watchers.letters > 0 || watchers.opponent) &&
        draftIdentity === inputIdentity,
      matchId: match.matchId,
      row: rowCount,
      cells: toDraftCellsWire(input.cells),
    });
  }, [
    draftIdentity,
    draftRelay,
    input.cells,
    inputIdentity,
    match,
    myTurnOver,
    phase,
    rowCount,
    watchers.letters,
    watchers.opponent,
  ]);

  const requestOpponentWatch = useCallback(
    (matchId: string) => {
      useDuelStore.getState().beginOpponentWatch(matchId);
      setOpponentWatchBusy(true);
      getSocket()
        .timeout(5000)
        .emit(
          "opponent:watch",
          { matchId },
          (timeout: Error | null, res: AckResult<OpponentWatchState>) => {
            setOpponentWatchBusy(false);
            if (timeout) {
              getSocket().emit("opponent:unwatch", { matchId });
              useDuelStore.getState().closeOpponentWatch();
              warn(messages.feedback.opponentWatchFailed);
              return;
            }
            if (!res?.ok) {
              getSocket().emit("opponent:unwatch", { matchId });
              useDuelStore.getState().closeOpponentWatch();
              warn(res ? errorMessage(res) : errors.opponent_watch_failed());
              return;
            }
            useDuelStore.getState().openOpponentWatch(res.data);
          },
        );
    },
    [warn],
  );

  const watchOpponent = useCallback(() => {
    const current = useDuelStore.getState();
    if (!current.match || current.phase !== "live") return;
    if (current.opponentWatchRequestedFor === current.match.matchId) return;
    requestOpponentWatch(current.match.matchId);
  }, [requestOpponentWatch]);

  const autoWatchAttemptedFor = useRef<string | null>(null);
  useEffect(() => {
    const current = useDuelStore.getState();
    const matchId = current.match?.matchId;
    if (!matchId) {
      autoWatchAttemptedFor.current = null;
      return;
    }
    if (current.opponentWatchRequestedFor === matchId) {
      autoWatchAttemptedFor.current = matchId;
      return;
    }
    if (!shouldAutoWatchOpponent({
      matchId,
      phase: current.phase,
      myTurnOver,
      opponentSolved: current.opponentSolvedAt !== null,
      requestedFor: current.opponentWatchRequestedFor,
      attemptedFor: autoWatchAttemptedFor.current,
    }) || !ownTurnRevealed) return;
    autoWatchAttemptedFor.current = matchId;
    requestOpponentWatch(matchId);
  }, [
    match?.matchId,
    myTurnOver,
    opponentSolvedAt,
    opponentWatchRequestedFor,
    ownTurnRevealed,
    phase,
    requestOpponentWatch,
  ]);

  const unwatchOpponent = useCallback(() => {
    const current = useDuelStore.getState();
    const matchId = current.opponentWatchRequestedFor;
    if (matchId) getSocket().emit("opponent:unwatch", { matchId });
    current.closeOpponentWatch();
    setOpponentWatchBusy(false);
  }, []);

  useEffect(() => {
    const socket = getSocket();
    const onConnect = () => {
      const current = useDuelStore.getState();
      duelAudioState.current = EMPTY_DUEL_AUDIO_STATE;
      const currentlySolved = current.rows.some((row) =>
        row.pattern.every((cell) => cell === "correct"),
      );
      solvedAudioState.current = {
        matchId: current.match?.matchId ?? current.result?.matchId ?? null,
        solved: currentlySolved || Boolean(current.result?.you.solved),
        played: currentlySolved || Boolean(current.result?.you.solved),
      };
      stopTimerSounds();
      if (
        current.phase === "live" &&
        current.opponentWatchRequestedFor === current.match?.matchId
      ) {
        requestOpponentWatch(current.match.matchId);
      }
    };
    socket.on("connect", onConnect);
    return () => {
      socket.off("connect", onConnect);
    };
  }, [requestOpponentWatch]);

  useEffect(
    () => () => {
      const current = useDuelStore.getState();
      if (current.opponentWatchRequestedFor) {
        getSocket().emit("opponent:unwatch", { matchId: current.opponentWatchRequestedFor });
        current.closeOpponentWatch();
      }
    },
    [],
  );

  const keyboard = useMemo(
    () =>
      rows.reduce<Record<string, LetterState>>(
        (acc, row) => mergeKeyboardStates(acc, row.word, row.pattern),
        {},
      ),
    [rows],
  );

  const reset = useCallback(() => useDuelStore.getState().reset(), []);

  const forfeit = useCallback(async (): Promise<boolean> => {
    const matchId = useDuelStore.getState().match?.matchId;
    if (!matchId) return false;

    useDuelStore.getState().beginExit(matchId);
    return new Promise((resolve) => {
      // Yanıt gelmezse onay penceresi "bekleniyor"da kilitli kalmasın.
      getSocket()
        .timeout(8000)
        .emit(
          "match:forfeit",
          { matchId },
          (timeout: Error | null, res: AckResult<{ forfeited: true }>) => {
            if (timeout) {
              useDuelStore.getState().cancelExit();
              warn(messages.feedback.serverUnavailable);
              resolve(false);
              return;
            }
            if (!res?.ok) {
              useDuelStore.getState().cancelExit();
              warn(res ? errorMessage(res) : errors.forfeit_failed());
              resolve(false);
              return;
            }
            useDuelStore.getState().reset();
            resolve(true);
          },
        );
    });
  }, [warn]);

  /* --- Rövanş -------------------------------------------------------- */

  const askRematch = useCallback(() => {
    const matchId = result?.matchId;
    if (!matchId) return;
    getSocket().emit("rematch:offer", { matchId }, (res: AckResult<{ offered: true }>) => {
      if (!res?.ok) {
        warn(res ? errorMessage(res) : errors.rematch_response_failed());
        return;
      }
      useDuelStore.getState().setRematchSent(matchId);
    });
  }, [result, warn]);

  const answerRematch = useCallback(
    (accept: boolean) => {
      const offer = useDuelStore.getState().rematchOffer;
      if (!offer) return;
      useDuelStore.getState().clearRematchOffer();
      getSocket().emit(
        "rematch:respond",
        { matchId: offer.matchId, accept },
        (res: AckResult<{ ok: true }>) => {
          if (!res?.ok) warn(res ? errorMessage(res) : errors.rematch_response_failed());
        },
      );
    },
    [warn],
  );

  return {
    phase,
    /** Tahmin sunucuda; klavye bu sırada tepki vermiyor ve bunu göstermeli. */
    sending,
    match,
    rows,
    opponentRows,
    opponentSolved: opponentSolvedAt !== null,
    opponentSolvedAt,
    attemptLimit,
    result,
    watchers,
    opponentWatch,
    opponentWatchBusy,
    opponentWatching: opponentWatchRequestedFor !== null,
    watchOpponent,
    unwatchOpponent,
    rematchOffer,
    rematchSentFor,
    rematchNotice,
    askRematch,
    answerRematch,
    remainingMs,
    countdownMs: match ? Math.max(0, match.startsAt - serverNow()) : 0,
    solved,
    myTurnOver,
    ownTurnRevealed,
    cells: input.cells,
    pinned: input.pinned,
    selectCell: input.selectCell,
    press: input.press,
    keyboard,
    toast,
    shake,
    findOpponent,
    cancelSearch,
    forfeit,
    reset,
  };
}
